import {
  Button,
  Calendar,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
} from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Command } from "cmdk";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { CalendarIcon, Check, ChevronDown, ChevronsUpDown, ChevronUp, Plus, Trash2 } from "@/components/icons";
import { EditDialog, SettingRow } from "@/components/setting-row";
import { updateSiteConfig } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { currentDateFnsLocale } from "@/lib/locale-format";
import {
  DEFAULT_TIMELINE_ICON,
  TIMELINE_ICON_OPTIONS,
  getTimelineIcon,
} from "@/lib/timeline-icons";
import type { TimelineConfig, TimelineEvent, TimelineIconKey } from "@/lib/types";
import { cn } from "@/lib/utils";

const MAX_DAYS = 14;
const MAX_EVENTS_TOTAL = 100;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** All IANA zones the runtime knows; empty on very old engines. */
const TIME_ZONES: string[] = (() => {
  const fn = (Intl as { supportedValuesOf?: (key: "timeZone") => string[] })
    .supportedValuesOf;
  return typeof fn === "function" ? fn("timeZone") : [];
})();

const DEFAULT_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface DraftEvent {
  id: string;
  time: string;
  name: string;
  description: string;
  icon: TimelineIconKey;
}

interface DraftDay {
  id: string;
  date: string;
  label: string;
  events: DraftEvent[];
}

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function blankEvent(): DraftEvent {
  return { id: makeId(), time: "12:00", name: "", description: "", icon: DEFAULT_TIMELINE_ICON };
}

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromDateString(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Adds `days` to a "YYYY-MM-DD" string, returning a new "YYYY-MM-DD". */
function addDays(value: string, days: number): string {
  const base = fromDateString(value) ?? new Date();
  base.setDate(base.getDate() + days);
  return toDateString(base);
}

function toDraftEvents(events: TimelineEvent[] | undefined): DraftEvent[] {
  return (events ?? []).map((e) => ({
    id: e.id,
    time: e.time,
    name: e.name,
    description: e.description ?? "",
    icon: e.icon,
  }));
}

/**
 * Builds the editable day list from a stored timeline, tolerating the legacy
 * flat shape (top-level `events`/`date`) by wrapping it in a single day.
 */
function toDraftDays(timeline: TimelineConfig | undefined): DraftDay[] {
  if (!timeline) return [];
  const legacy = timeline as TimelineConfig & {
    events?: TimelineEvent[];
    date?: string;
  };
  if (!Array.isArray(timeline.days) && Array.isArray(legacy.events)) {
    return [
      {
        id: makeId(),
        date: legacy.date ?? "",
        label: "",
        events: toDraftEvents(legacy.events),
      },
    ];
  }
  return (timeline.days ?? []).map((d) => ({
    id: d.id,
    date: d.date,
    label: d.label ?? "",
    events: toDraftEvents(d.events),
  }));
}

function blankDay(existing: DraftDay[]): DraftDay {
  const maxDate = existing.reduce((mx, d) => (d.date > mx ? d.date : mx), "");
  const date = maxDate ? addDays(maxDate, 1) : toDateString(new Date());
  return { id: makeId(), date, label: "", events: [blankEvent()] };
}

/** Stable string for dirty comparison (text trimmed). */
function serialize(days: DraftDay[], timeZone: string, showDates: boolean): string {
  return JSON.stringify({
    timeZone,
    showDates,
    days: days.map((d) => ({
      date: d.date,
      label: d.label.trim(),
      events: d.events.map((e) => ({
        time: e.time,
        name: e.name.trim(),
        description: e.description.trim(),
        icon: e.icon,
      })),
    })),
  });
}

function TimeZoneCombobox({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    const set = new Set<string>(TIME_ZONES);
    if (DEFAULT_TIME_ZONE) set.add(DEFAULT_TIME_ZONE);
    if (value) set.add(value);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("h-10 w-full justify-between", !value && "text-muted-foreground")}
        >
          <span className="truncate">{value || t("timelineEditor.noTimeZone")}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <Command.Input
            className="h-10 w-full border-b bg-transparent px-3 text-base text-foreground outline-none placeholder:text-muted-foreground"
            placeholder={t("timelineEditor.searchTimeZone")}
          />
          <Command.List className="ui-scrollbar max-h-72 overflow-y-auto p-1">
            <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t("timelineEditor.noTimeZoneFound")}
            </Command.Empty>
            {options.map((tz) => (
              <Command.Item
                key={tz}
                value={tz}
                onSelect={() => {
                  onChange(tz);
                  setOpen(false);
                }}
                className={cn(
                  "flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm",
                  "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
                )}
              >
                <span className="truncate">{tz}</span>
                <Check className={cn("ml-2 h-4 w-4", value === tz ? "opacity-100" : "opacity-0")} />
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function IconPicker({
  value,
  onChange,
}: {
  value: TimelineIconKey;
  onChange: (key: TimelineIconKey) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Current = getTimelineIcon(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="icon" aria-label={t("timelineEditor.chooseIcon")}>
          <Current className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="grid grid-cols-6 gap-1">
          {TIMELINE_ICON_OPTIONS.map(({ key, labelKey, Icon }) => (
            <Button
              key={key}
              type="button"
              variant={key === value ? "default" : "ghost"}
              size="icon"
              title={t(labelKey)}
              aria-label={t(labelKey)}
              onClick={() => {
                onChange(key);
                setOpen(false);
              }}
            >
              <Icon className="h-4 w-4" />
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DayCard({
  day,
  open,
  onToggle,
  onChangeDate,
  onChangeLabel,
  onAddEvent,
  onUpdateEvent,
  onRemoveEvent,
  onRemoveDay,
}: {
  day: DraftDay;
  open: boolean;
  onToggle: () => void;
  onChangeDate: (date: string) => void;
  onChangeLabel: (label: string) => void;
  onAddEvent: () => void;
  onUpdateEvent: (eventId: string, patch: Partial<DraftEvent>) => void;
  onRemoveEvent: (eventId: string) => void;
  onRemoveDay: () => void;
}) {
  const { t } = useTranslation();
  const dateValue = fromDateString(day.date);
  const eventCount = day.events.length;

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={onToggle}
          aria-expanded={open}
        >
          {open ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">
              {dateValue
                ? format(dateValue, "PPP", { locale: currentDateFnsLocale() })
                : t("timelineEditor.pickDate")}
              {day.label.trim() ? `, ${day.label.trim()}` : ""}
            </span>
            <span className="block text-xs text-muted-foreground">
              {eventCount === 0
                ? t("timelineEditor.noEvents")
                : t("timelineEditor.events", { count: eventCount })}
            </span>
          </span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("timelineEditor.removeDay")}
          onClick={onRemoveDay}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {open && (
        <div className="space-y-4 border-t p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("timelineEditor.dateLabel")}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "w-full justify-start",
                      !day.date && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon />
                    {dateValue
                      ? format(dateValue, "PPP", { locale: currentDateFnsLocale() })
                      : t("timelineEditor.pickDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateValue ?? undefined}
                    onSelect={(d) => onChangeDate(d ? toDateString(d) : "")}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>{t("timelineEditor.labelOptional")}</Label>
              <Input
                placeholder={t("timelineEditor.labelPlaceholder")}
                value={day.label}
                onChange={(e) => onChangeLabel(e.target.value)}
              />
            </div>
          </div>

          {day.events.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t("timelineEditor.noEventsOnDay")}
            </p>
          )}
          {day.events.map((e) => (
            <div key={e.id} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2">
                <IconPicker
                  value={e.icon}
                  onChange={(icon) => onUpdateEvent(e.id, { icon })}
                />
                <Input
                  type="time"
                  aria-label={t("timelineEditor.timeAria")}
                  value={e.time}
                  onChange={(ev) => onUpdateEvent(e.id, { time: ev.target.value })}
                  className="w-32"
                />
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("timelineEditor.removeEvent")}
                  onClick={() => onRemoveEvent(e.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Input
                placeholder={t("timelineEditor.eventNamePlaceholder")}
                value={e.name}
                onChange={(ev) => onUpdateEvent(e.id, { name: ev.target.value })}
              />
              <Input
                placeholder={t("timelineEditor.descriptionPlaceholder")}
                value={e.description}
                onChange={(ev) => onUpdateEvent(e.id, { description: ev.target.value })}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onAddEvent}
          >
            <Plus className="h-4 w-4" /> {t("timelineEditor.addEvent")}
          </Button>
        </div>
      )}
    </div>
  );
}

export function TimelineSection({
  siteId,
  timeline,
  allowTimeline,
}: {
  siteId: string;
  timeline?: TimelineConfig;
  allowTimeline: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [openDays, setOpenDays] = useState<Set<string>>(new Set());

  const initialDays = useMemo(() => toDraftDays(timeline), [timeline]);
  const initialTimeZone = timeline?.timeZone || DEFAULT_TIME_ZONE;
  const initialShowDates = timeline?.showDates !== false;

  const [draftDays, setDraftDays] = useState<DraftDay[]>(initialDays);
  const [draftTimeZone, setDraftTimeZone] = useState(initialTimeZone);
  const [draftShowDates, setDraftShowDates] = useState(initialShowDates);

  const totalEvents = draftDays.reduce((n, d) => n + d.events.length, 0);

  const mutation = useMutation({
    mutationFn: () => {
      const days = draftDays.map((d) => ({
        id: d.id,
        date: d.date,
        ...(d.label.trim() ? { label: d.label.trim() } : {}),
        events: d.events.map((e) => ({
          id: e.id,
          time: e.time,
          name: e.name.trim(),
          ...(e.description.trim() ? { description: e.description.trim() } : {}),
          icon: e.icon,
        })),
      }));
      const payload: TimelineConfig = {
        ...(draftTimeZone ? { timeZone: draftTimeZone } : {}),
        showDates: draftShowDates,
        days,
      };
      return updateSiteConfig({ siteId, configPatch: { timeline: payload } });
    },
    onSuccess: async () => {
      toast.success(t("timelineEditor.updated"));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["site", siteId] }),
        queryClient.invalidateQueries({ queryKey: ["sites"] }),
      ]);
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const openDialog = () => {
    const days = toDraftDays(timeline);
    setDraftDays(days);
    setDraftTimeZone(initialTimeZone);
    setDraftShowDates(initialShowDates);
    setOpenDays(new Set(days.length <= 1 ? days.map((d) => d.id) : []));
    setOpen(true);
  };

  const toggleDay = (id: string) =>
    setOpenDays((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const addDay = () => {
    const day = blankDay(draftDays);
    setDraftDays((prev) => [...prev, day]);
    setOpenDays((s) => new Set(s).add(day.id));
  };
  const removeDay = (dayId: string) => {
    setDraftDays((prev) => prev.filter((d) => d.id !== dayId));
    setOpenDays((s) => {
      const next = new Set(s);
      next.delete(dayId);
      return next;
    });
  };
  const patchDay = (dayId: string, patch: Partial<Omit<DraftDay, "events" | "id">>) =>
    setDraftDays((prev) => prev.map((d) => (d.id === dayId ? { ...d, ...patch } : d)));
  const addEvent = (dayId: string) =>
    setDraftDays((prev) =>
      prev.map((d) =>
        d.id === dayId ? { ...d, events: [...d.events, blankEvent()] } : d,
      ),
    );
  const removeEvent = (dayId: string, eventId: string) =>
    setDraftDays((prev) =>
      prev.map((d) =>
        d.id === dayId
          ? { ...d, events: d.events.filter((e) => e.id !== eventId) }
          : d,
      ),
    );
  const updateEvent = (
    dayId: string,
    eventId: string,
    patch: Partial<DraftEvent>,
  ) =>
    setDraftDays((prev) =>
      prev.map((d) =>
        d.id === dayId
          ? {
              ...d,
              events: d.events.map((e) =>
                e.id === eventId ? { ...e, ...patch } : e,
              ),
            }
          : d,
      ),
    );

  const valid =
    totalEvents <= MAX_EVENTS_TOTAL &&
    draftDays.every(
      (d) =>
        fromDateString(d.date) !== null &&
        d.events.every((e) => TIME_RE.test(e.time) && e.name.trim() !== ""),
    );
  const dirty =
    serialize(draftDays, draftTimeZone, draftShowDates) !==
    serialize(initialDays, initialTimeZone, initialShowDates);

  const summaryDays = initialDays.length;
  const summaryEvents = initialDays.reduce((n, d) => n + d.events.length, 0);
  const summaryFirstDate = initialDays
    .map((d) => d.date)
    .filter((value) => value !== "")
    .sort((a, b) => a.localeCompare(b))[0];
  const summaryDateValue = summaryFirstDate
    ? fromDateString(summaryFirstDate)
    : null;

  return (
    <>
      <SettingRow
        title={t("timelineEditor.title")}
        description={t("timelineEditor.desc")}
        action={
          <Button type="button" variant="ghost" size="sm" onClick={openDialog}>
            {t("common.edit")}
          </Button>
        }
        value={
          <div className="space-y-1">
            <p>
              {t("timelineEditor.title")}:{" "}
              <span className="font-medium">
                {allowTimeline ? t("siteDetail.permissions.on") : t("siteDetail.permissions.off")}
              </span>
              {!allowTimeline && (
                <span className="text-muted-foreground"> ({t("timelineEditor.turnOnHint")})</span>
              )}
            </p>
            <p className="text-muted-foreground">
              {summaryEvents === 0
                ? t("timelineEditor.noEventsYet")
                : t("timelineEditor.events", { count: summaryEvents })}
              {summaryDays > 1 ? `, ${t("timelineEditor.days", { count: summaryDays })}` : ""}
              {summaryDateValue
                ? `, ${format(summaryDateValue, "PPP", { locale: currentDateFnsLocale() })}`
                : ""}
            </p>
          </div>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("timelineEditor.editTitle")}
        description={t("timelineEditor.editDesc")}
        onSave={() => mutation.mutate()}
        saving={mutation.isPending}
        saveDisabled={!dirty || !valid}
        contentClassName="sm:max-w-2xl"
      >
        <div className="space-y-2 rounded-md border p-3">
          <Label>{t("timelineEditor.timeZone")}</Label>
          <TimeZoneCombobox value={draftTimeZone} onChange={setDraftTimeZone} />
          <p className="text-xs text-muted-foreground">
            {t("timelineEditor.timeZoneHint")}
          </p>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border p-3">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="timeline-show-dates">{t("timelineEditor.showDates")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("timelineEditor.showDatesHint")}
            </p>
          </div>
          <Switch
            id="timeline-show-dates"
            checked={draftShowDates}
            onCheckedChange={setDraftShowDates}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{t("timelineEditor.daysLabel")}</Label>
            <span className="text-xs text-muted-foreground">
              {t("timelineEditor.eventsCount", { count: totalEvents, max: MAX_EVENTS_TOTAL })}
            </span>
          </div>
          {draftDays.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t("timelineEditor.noDays")}
            </p>
          )}
          {draftDays.map((day) => (
            <DayCard
              key={day.id}
              day={day}
              open={openDays.has(day.id)}
              onToggle={() => toggleDay(day.id)}
              onChangeDate={(date) => patchDay(day.id, { date })}
              onChangeLabel={(label) => patchDay(day.id, { label })}
              onAddEvent={() => addEvent(day.id)}
              onUpdateEvent={(eventId, patch) => updateEvent(day.id, eventId, patch)}
              onRemoveEvent={(eventId) => removeEvent(day.id, eventId)}
              onRemoveDay={() => removeDay(day.id)}
            />
          ))}
          {draftDays.length < MAX_DAYS && (
            <Button type="button" variant="outline" className="w-full" onClick={addDay}>
              <Plus className="h-4 w-4" /> {t("timelineEditor.addDay")}
            </Button>
          )}
        </div>
      </EditDialog>
    </>
  );
}
